import { useCallback } from "react"
import { IconCopyFill18 } from "nucleo-ui-fill-18"
import {
	IconClipboardContentOutline18 as ClipboardContent,
	IconClipboardOutline18 as Clipboard,
	IconCopyIdOutline18 as CopyId,
	IconLink2Outline18 as Link2,
	IconLink3Outline18 as Link3,
	IconLinkOutline18 as Link,
	IconMarkdownOutline18 as Markdown,
	IconTextPromptOutline18 as TextPrompt,
	IconTextTitleCaseOutline18 as TextTitle,
} from "nucleo-ui-outline-18"

import type { IconType } from "@productlane/ui/src/icons"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"

import type { RouterInputs } from "@/lib/api"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { copyRichText, copyTextWhenReady } from "@/lib/clipboard"
import { useThreadContext } from "../../thread-context"
import { hasTextSelection, threadTitleLink, threadUrl } from "./thread-copy"

const { CMD, SHIFT, ALT } = ShortcutSymbols

type ThreadCopyFormat = RouterInputs["thread"]["getCopyMarkdown"]["format"]

const MARKDOWN_COPIED: Record<ThreadCopyFormat, string> = {
	thread: "Copied thread as Markdown",
	everything: "Copied everything as Markdown",
	prompt: "Copied thread as a prompt",
}

async function copyWithToast(
	kind: string,
	title: string,
	write: () => Promise<void>,
	shortcut?: boolean,
) {
	try {
		await write()
		toast.info({ title, icon: IconCopyFill18 })
		analytics.track("thread-copy", { kind, shortcut })
	} catch {
		toast.error({
			title: "Couldn't copy to the clipboard",
			icon: IconCopyFill18,
		})
	}
}

export function useThreadCopyActions() {
	const { thread } = useThreadContext()
	const utils = api.useUtils()
	const title = thread.title?.trim() ?? ""

	const copyId = useCallback(
		(shortcut?: boolean) => {
			if (thread.number == null) return
			const id = `#${thread.number}`
			void copyWithToast(
				"id",
				`Copied ${id}`,
				() => navigator.clipboard.writeText(id),
				shortcut,
			)
		},
		[thread.number],
	)

	const copyUrl = useCallback(
		(shortcut?: boolean) =>
			void copyWithToast(
				"url",
				"Copied thread link",
				() =>
					navigator.clipboard.writeText(
						threadUrl(window.location.origin, thread.id),
					),
				shortcut,
			),
		[thread.id],
	)

	const copyTitle = useCallback(
		(shortcut?: boolean) => {
			if (!title) return
			void copyWithToast(
				"title",
				"Copied thread title",
				() => navigator.clipboard.writeText(title),
				shortcut,
			)
		},
		[title],
	)

	const copyTitleAsLink = useCallback(
		(shortcut?: boolean) =>
			void copyWithToast(
				"title-link",
				"Copied title as link",
				() =>
					copyRichText(
						threadTitleLink(
							{ id: thread.id, number: thread.number, title },
							window.location.origin,
						),
					),
				shortcut,
			),
		[thread.id, thread.number, title],
	)

	const copyMarkdown = useCallback(
		(format: ThreadCopyFormat, shortcut?: boolean) =>
			void copyWithToast(
				`markdown-${format}`,
				MARKDOWN_COPIED[format],
				() =>
					copyTextWhenReady(
						utils.thread.getCopyMarkdown
							.fetch({ threadId: thread.id, format }, { staleTime: 0 })
							.then((r) => r.markdown),
					),
				shortcut,
			),
		[utils, thread.id],
	)

	useShortcut("mod+shift+comma", () => copyUrl(true), { preventDefault: true })
	useShortcut("mod+shift+quote", () => copyTitle(true), {
		preventDefault: true,
		enabled: !!title,
	})
	useShortcut("mod+c", () => copyTitleAsLink(true), {
		preventDefault: true,
		ignoreEventWhen: () => hasTextSelection(window.getSelection()),
	})
	useShortcut("mod+alt+c", () => copyMarkdown("everything", true), {
		preventDefault: true,
	})
	useShortcut("mod+alt+p", () => copyMarkdown("prompt", true), {
		preventDefault: true,
	})

	return {
		copyId,
		copyUrl,
		copyTitle,
		copyTitleAsLink,
		copyMarkdown,
		hasNumber: thread.number != null,
		hasTitle: !!title,
	}
}

interface Props {
	actions: ReturnType<typeof useThreadCopyActions>
	/** Present only when the workspace's support portal is on. */
	onCopySupportPortalLink?: () => void
}

export function ThreadCopySubmenu({ actions, onCopySupportPortalLink }: Props) {
	const items: {
		key: string
		label: string
		Icon: IconType
		shortcut?: string
		onClick: () => void
		hidden?: boolean
	}[] = [
		{
			key: "id",
			label: "Copy ID",
			Icon: CopyId,
			shortcut: `${CMD}.`,
			onClick: () => actions.copyId(),
			hidden: !actions.hasNumber,
		},
		{
			key: "url",
			label: "Copy URL",
			Icon: Link,
			shortcut: `${CMD}${SHIFT},`,
			onClick: () => actions.copyUrl(),
		},
		{
			key: "portal",
			label: "Copy Support Portal link",
			Icon: Link2,
			shortcut: `${SHIFT}${ShortcutSymbols.S}`,
			onClick: () => onCopySupportPortalLink?.(),
			hidden: !onCopySupportPortalLink,
		},
		{
			key: "title",
			label: "Copy title",
			Icon: TextTitle,
			shortcut: `${CMD}${SHIFT}'`,
			onClick: () => actions.copyTitle(),
			hidden: !actions.hasTitle,
		},
		{
			key: "title-link",
			label: "Copy title as link",
			Icon: Link3,
			shortcut: `${CMD}${ShortcutSymbols.C}`,
			onClick: () => actions.copyTitleAsLink(),
		},
		{
			key: "markdown-thread",
			label: "Copy thread as Markdown",
			Icon: Markdown,
			onClick: () => actions.copyMarkdown("thread"),
		},
		{
			key: "markdown-everything",
			label: "Copy everything as Markdown",
			Icon: ClipboardContent,
			shortcut: `${CMD}${ALT}${ShortcutSymbols.C}`,
			onClick: () => actions.copyMarkdown("everything"),
		},
		{
			key: "prompt",
			label: "Copy as prompt",
			Icon: TextPrompt,
			shortcut: `${CMD}${ALT}${ShortcutSymbols.P}`,
			onClick: () => actions.copyMarkdown("prompt"),
		},
	]

	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger className="rounded-md" Icon={Clipboard}>
				Copy
			</DropdownMenuSubTrigger>
			<DropdownMenuPortal>
				<DropdownMenuSubContent className="w-72" sideOffset={6}>
					{items
						.filter((item) => !item.hidden)
						.map((item) => (
							<DropdownMenuItem
								key={item.key}
								Icon={item.Icon}
								shortcut={item.shortcut}
								onClick={item.onClick}
							>
								{item.label}
							</DropdownMenuItem>
						))}
				</DropdownMenuSubContent>
			</DropdownMenuPortal>
		</DropdownMenuSub>
	)
}
